// src/pages/AttendanceSystem.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, TextField, Alert, IconButton,
  Tooltip, LinearProgress
} from '@mui/material';
import {
  CheckCircle, Cancel, Schedule, People, TrendingUp,
  Add, Edit, Visibility, Today, CalendarMonth
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

const AttendanceSystem = () => {
  const { t } = useI18n();
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const staffData = await getData('staff') || [];
      const attendanceData = await getData('attendance_records') || [];
      
      setStaff(staffData.filter(s => s.status === 'active'));
      setAttendance(attendanceData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTodaysAttendance = () => {
    return attendance.filter(record => record.date === selectedDate);
  };

  const getEmployeeAttendance = (employeeId) => {
    return getTodaysAttendance().find(record => record.employeeId === employeeId);
  };

  const handleMarkAttendance = async (employeeId, status, reason = '') => {
    try {
      const attendanceRecords = await getData('attendance_records') || [];
      
      // Видаляємо старий запис для цього дня, якщо він є
      const filteredRecords = attendanceRecords.filter(
        record => !(record.employeeId === employeeId && record.date === selectedDate)
      );
      
      const newRecord = {
        id: Date.now(),
        employeeId,
        date: selectedDate,
        status,
        reason,
        markedBy: 'supervisor',
        createdAt: new Date().toISOString()
      };
      
      filteredRecords.push(newRecord);
      await saveData('attendance_records', filteredRecords);
      setAttendance(filteredRecords);
      setAttendanceDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error("Error marking attendance:", error);
    }
  };

  const openAttendanceDialog = (employee) => {
    setSelectedEmployee(employee);
    setAttendanceDialogOpen(true);
  };

  // Статистика за день
  const dailyStats = {
    total: staff.length,
    present: getTodaysAttendance().filter(a => a.status === 'present').length,
    absent: getTodaysAttendance().filter(a => a.status === 'absent').length,
    late: getTodaysAttendance().filter(a => a.status === 'late').length,
    marked: getTodaysAttendance().length
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('attendance.system_title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('attendance.system_subtitle')}
        </Typography>
      </Box>

      {/* Панель керування */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label={t('attendance.select_date')}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120, textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                    {dailyStats.present}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('attendance.present')}
                  </Typography>
                </Card>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120, textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                    {dailyStats.absent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('attendance.absent')}
                  </Typography>
                </Card>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120, textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                    {dailyStats.late}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('attendance.late')}
                  </Typography>
                </Card>
                <Card variant="outlined" sx={{ flex: 1, minWidth: 120, textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                    {dailyStats.marked}/{dailyStats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('attendance.marked')}
                  </Typography>
                </Card>
              </Box>
            </Grid>
          </Grid>

          {/* Прогрес */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                {t('attendance.completion')}: {Math.round((dailyStats.marked / dailyStats.total) * 100)}%
              </Typography>
              <Typography variant="body2">
                {dailyStats.marked} / {dailyStats.total}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(dailyStats.marked / dailyStats.total) * 100}
              sx={{ height: 8, borderRadius: 4 }}
              color={dailyStats.marked === dailyStats.total ? 'success' : 'primary'}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Таблиця персоналу */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <People color="primary" />
              {t('attendance.staff_list')} ({staff.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(selectedDate).toLocaleDateString()}
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('attendance.employee')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('attendance.department')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('attendance.position')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('attendance.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('attendance.reason')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('attendance.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map(employee => {
                  const attendanceRecord = getEmployeeAttendance(employee.id);
                  
                  return (
                    <TableRow key={employee.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {employee.name || `${employee.firstName} ${employee.lastName}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t(`roles.${employee.role}`)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{employee.department}</TableCell>
                      <TableCell>{employee.position || '-'}</TableCell>
                      <TableCell>
                        {attendanceRecord ? (
                          <Chip 
                            label={t(`attendance.status.${attendanceRecord.status}`)}
                            color={
                              attendanceRecord.status === 'present' ? 'success' :
                              attendanceRecord.status === 'absent' ? 'error' :
                              attendanceRecord.status === 'late' ? 'warning' : 'default'
                            }
                            size="small"
                          />
                        ) : (
                          <Chip 
                            label={t('attendance.not_marked')}
                            color="default"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {attendanceRecord?.reason || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={t('attendance.mark_attendance')}>
                            <IconButton 
                              size="small"
                              onClick={() => openAttendanceDialog(employee)}
                              color="primary"
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('attendance.view_profile')}>
                            <IconButton 
                              size="small"
                              component={Link}
                              to={`/employee/${employee.id}`}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог відмітки присутності */}
      <AttendanceDialog
        open={attendanceDialogOpen}
        onClose={() => {
          setAttendanceDialogOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onSave={handleMarkAttendance}
        existingRecord={selectedEmployee ? getEmployeeAttendance(selectedEmployee.id) : null}
        t={t}
      />
    </Box>
  );
};

// Діалог відмітки присутності
const AttendanceDialog = ({ open, onClose, employee, onSave, existingRecord, t }) => {
  const [status, setStatus] = useState(existingRecord?.status || 'present');
  const [reason, setReason] = useState(existingRecord?.reason || '');

  const handleSave = () => {
    onSave(employee.id, status, reason);
    setStatus('present');
    setReason('');
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('attendance.mark_attendance_for')} {employee.name || `${employee.firstName} ${employee.lastName}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t('attendance.status')}</InputLabel>
            <Select
              value={status}
              label={t('attendance.status')}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="present">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  {t('attendance.status.present')}
                </Box>
              </MenuItem>
              <MenuItem value="absent">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Cancel color="error" />
                  {t('attendance.status.absent')}
                </Box>
              </MenuItem>
              <MenuItem value="late">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule color="warning" />
                  {t('attendance.status.late')}
                </Box>
              </MenuItem>
              <MenuItem value="vacation">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Today color="info" />
                  {t('attendance.status.vacation')}
                </Box>
              </MenuItem>
              <MenuItem value="sick">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Cancel color="warning" />
                  {t('attendance.status.sick')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          
          {(status === 'absent' || status === 'late' || status === 'sick') && (
            <TextField
              fullWidth
              label={t('attendance.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={3}
              placeholder={t('attendance.reason_placeholder')}
            />
          )}

          {existingRecord && (
            <Alert severity="info">
              {t('attendance.existing_record')}: {new Date(existingRecord.date).toLocaleDateString()} - {t(`attendance.status.${existingRecord.status}`)}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {existingRecord ? t('attendance.update') : t('attendance.mark')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceSystem;